// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import { InvoiceModel, InvoiceSettingsModel } from "@insite/client-framework/Types/ApiModels";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import Clickable from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import Icon from "@insite/mobius/Icon";
import Printer from "@insite/mobius/Icons/Printer";
import { labels } from "@steiner/Overrides/Widgets/InvoiceHistory/InvoiceHistoryTable";
import { EMPTY_VALUE } from "@steiner/XDL/constants";
import React, { FC } from "react";

interface OwnProps {
    invoice: InvoiceModel;
    session: Session;
    invoiceSettings: InvoiceSettingsModel;
    extendedStyles?: InvoiceSummaryCardStyles;
}

type Props = OwnProps;

export interface InvoiceSummaryCardStyles {
    dataCell?: DataTableCellProps;
}

export const invoiceSummaryCardStyles: InvoiceSummaryCardStyles = {};

const InvoiceSummaryCard: FC<Props> = ({ invoice, session, invoiceSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));

    const data = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        date: invoice.invoiceDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.invoiceDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        dueDate: invoice.dueDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.dueDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        shipTo: `${invoice.stCompanyName} ${invoice.btAddress1} ${invoice.btAddress2} ${invoice.shipToCity} ${invoice.shipToState}`,
        total: invoice.invoiceTotalDisplay,
    };

    return (
        <>
            <DataTableCell {...styles.dataCell} {...{ header: labels.invoiceDate }}>
                <InvoiceDetailPageTypeLink title={data.date || EMPTY_VALUE} invoiceNumber={invoice.invoiceNumber} />
            </DataTableCell>
            <DataTableCell {...styles.dataCell} {...{ header: labels.invoiceNumber }}>
                <InvoiceDetailPageTypeLink
                    title={data.invoiceNumber || EMPTY_VALUE}
                    invoiceNumber={invoice.invoiceNumber}
                />
            </DataTableCell>
            <DataTableCell {...styles.dataCell} {...{ header: labels.dueDate }}>
                {data.dueDate || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...styles.dataCell} {...{ header: labels.invoiceTotal }}>
                {data.total || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell {...styles.dataCell} {...{ header: labels.actions }}>
                <StyledWrapper>
                    <Clickable
                        onClick={() => {
                            window.open(`/Invoice/GetPdf?invoiceNumber=${invoice.invoiceNumber}`);
                        }}
                        target="_blank"
                    >
                        <Icon src={Printer} />
                    </Clickable>
                </StyledWrapper>
            </DataTableCell>
        </>
    );
};

export default InvoiceSummaryCard;
